import { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') || '/';
  const { login } = useAuth();
  const submitLockRef = useRef(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || submitLockRef.current) return;
    submitLockRef.current = true;
    if (!formData.email || !formData.password) {
      toast.error('Lütfen tüm alanları doldurun');
      submitLockRef.current = false;
      return;
    }

    try {
      setIsSubmitting(true);
      console.log('🔐 LoginPage submit start email=', (formData.email || '').trim().toLowerCase());
      const result = await login(formData.email, formData.password);
      if (result.success) {
        toast.success('Giriş başarılı!');
        const isCourier = !!result?.isCourier;
        const isAdmin = !!result?.isAdmin;
        console.log('✅ Login ok isCourier=', isCourier, 'isAdmin=', isAdmin, 'redirect_to=', returnUrl);
        navigate(returnUrl);
      } else {
        const errText = String(result?.error || 'Giriş başarısız');
        console.warn('❌ LoginPage login failed', { error: errText });
        if (errText.toLowerCase().includes('email not confirmed')) {
          toast.error('Email doğrulaması gerekli. Lütfen mail kutunu kontrol et.');
        } else {
          toast.error(errText);
        }
      }
    } catch (e) {
      const msg =
        (e && typeof e === 'object' && (e.message || e.detail))
          ? (e.detail || e.message)
          : String(e || '');
      console.error('💥 LoginPage submit exception', e);
      toast.error(msg || 'Giriş sırasında beklenmeyen hata');
    } finally {
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4" data-testid="login-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <h1 className="font-manrope font-bold text-3xl text-trendyol-orange">Alışveriş Burada</h1>
            </Link>
            <p className="text-gray-600 mt-2">Hesabınıza giriş yapın</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                E-posta
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-trendyol-orange focus:outline-none transition-colors pl-11"
                  placeholder="ornek@email.com"
                  data-testid="login-email-input"
                />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Şifre
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-trendyol-orange focus:outline-none transition-colors"
                  placeholder="Şifrenizi girin"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
                  className="w-4 h-4 text-trendyol-orange border-gray-300 rounded focus:ring-trendyol-orange"
                  data-testid="remember-me-checkbox"
                />
                <span className="ml-2 text-sm text-gray-600">Beni Hatırla</span>
              </label>
              <Link to="/sifremi-unuttum" className="text-sm text-trendyol-orange hover:underline">
                Şifremi Unuttum
              </Link>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-trendyol-orange hover:bg-orange-600 text-white font-semibold py-3 rounded-lg transition-colors"
              data-testid="login-submit-button"
            >
              Giriş Yap
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Hesabınız yok mu?{' '}
              <Link to="/uyelik" className="text-trendyol-orange font-semibold hover:underline" data-testid="register-link">
                Üye Ol
              </Link>
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Giriş yaparak <Link to="#" className="text-trendyol-orange hover:underline">Kullanım Koşulları</Link> ve{' '}
              <Link to="#" className="text-trendyol-orange hover:underline">Gizlilik Politikası</Link>'nı kabul etmiş olursunuz.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}